/**
 * P4 — pure helpers for the report-branding page (unit-testable, no React/IO).
 */
import type {
  BrandingColors,
  BrandingFonts,
  BrandingMode,
  BrandingTokens,
  ReportBranding,
  ResolvedReportBranding,
  UpdateBrandingPatch,
} from '../../lib/api/client-reports/report-branding-api';

/** The 6 token colours, in display order, with human labels. */
export const COLOR_FIELDS: Array<{ key: keyof BrandingColors; label: string }> =
  [
    { key: 'bg', label: 'Background' },
    { key: 'panel', label: 'Panel' },
    { key: 'accent', label: 'Accent' },
    { key: 'accent_2', label: 'Accent 2' },
    { key: 'text', label: 'Text' },
    { key: 'text_muted', label: 'Muted text' },
  ];

export const FALLBACK_COLORS: BrandingColors = {
  bg: '#0e0f13',
  panel: '#191b22',
  accent: '#6c8cff',
  accent_2: '#33d9a6',
  text: '#f5f6fa',
  text_muted: '#a2a7b5',
};

export const FALLBACK_FONTS: BrandingFonts = {
  heading: 'Mona Sans, system-ui, sans-serif',
  body: 'Mona Sans, system-ui, sans-serif',
};

export function emptyTokens(mode: BrandingMode = 'dark'): BrandingTokens {
  return {
    mode,
    colors: { ...FALLBACK_COLORS },
    fonts: { ...FALLBACK_FONTS },
  };
}

/** Normalise a branding row's tokens, filling any missing field with fallback. */
export function normaliseTokens(
  t: BrandingTokens | null | undefined
): BrandingTokens {
  return {
    mode: t?.mode === 'light' ? 'light' : 'dark',
    colors: { ...FALLBACK_COLORS, ...(t?.colors ?? {}) },
    fonts: { ...FALLBACK_FONTS, ...(t?.fonts ?? {}) },
  };
}

/** The editable draft mirrored from a branding row (or a fresh default). */
export interface BrandingDraft {
  preset_key: string | null;
  tokens: BrandingTokens;
  design_md_override: string;
}

export function draftFromBranding(
  b: ReportBranding | null,
  presetKeyFallback: string | null = null
): BrandingDraft {
  return {
    preset_key: b?.preset_key ?? presetKeyFallback,
    tokens: normaliseTokens(b?.tokens),
    design_md_override: b?.design_md_override ?? '',
  };
}

/**
 * The draft for an agency with no saved branding row, seeded from the
 * branding central-api actually resolves for the agency (existing theme data
 * over the platform preset). Returns null when the payload has no tokens, so
 * the caller can fall back to a preset's default tokens.
 */
export function draftFromResolved(
  resolved: ResolvedReportBranding | null | undefined
): BrandingDraft | null {
  const tokens = resolved?.tokens;
  if (!tokens || typeof tokens !== 'object' || Array.isArray(tokens)) {
    return null;
  }
  return {
    preset_key: resolved?.preset?.key ?? null,
    tokens: normaliseTokens(tokens as BrandingTokens),
    design_md_override: '',
  };
}

function tokensEqual(a: BrandingTokens, b: BrandingTokens): boolean {
  if (a.mode !== b.mode) return false;
  for (const f of COLOR_FIELDS) {
    if ((a.colors[f.key] || '') !== (b.colors[f.key] || '')) return false;
  }
  return (
    (a.fonts.heading || '') === (b.fonts.heading || '') &&
    (a.fonts.body || '') === (b.fonts.body || '')
  );
}

/**
 * Compute the minimal PUT patch (only changed fields). Returns null when
 * nothing changed. `design_md_override` normalises '' ⇄ null (an empty editor
 * means "no override"). Colours/fonts/preset are compared structurally.
 */
export function buildBrandingPatch(
  original: ReportBranding | null,
  draft: BrandingDraft
): UpdateBrandingPatch | null {
  const patch: UpdateBrandingPatch = {};

  const origPreset = original?.preset_key ?? null;
  if ((draft.preset_key ?? null) !== origPreset) {
    patch.preset_key = draft.preset_key ?? null;
  }

  const origTokens = normaliseTokens(original?.tokens);
  if (!tokensEqual(origTokens, draft.tokens)) {
    patch.tokens = draft.tokens;
  }

  const origMd = (original?.design_md_override ?? '').trim();
  const draftMd = (draft.design_md_override ?? '').trim();
  if (origMd !== draftMd) {
    patch.design_md_override = draftMd === '' ? null : draft.design_md_override;
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

// ---------------------------------------------------------------------------
//  WCAG contrast hint (ported from client-portal/brand.ts math pattern).
// ---------------------------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] | null {
  let h = (hex || '').trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function relLuminance([r, g, b]: [number, number, number]): number {
  const chan = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
}

/** WCAG contrast ratio between two hex colours (1..21), or null if unparseable. */
export function contrastRatio(fg: string, bg: string): number | null {
  const a = hexToRgb(fg);
  const b = hexToRgb(bg);
  if (!a || !b) return null;
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export type ContrastRating = 'AAA' | 'AA' | 'AA Large' | 'Fail';

export function contrastRating(ratio: number | null): ContrastRating {
  if (ratio === null) return 'Fail';
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA Large';
  return 'Fail';
}
